import { resources } from "@constants/hardcoded-strings";
import { policyConfigDecisionOperatorsWithNoValue } from "@constants/policy-config-decision-attribute-field-result-options";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { TriggerTypes } from "@enums/trigger-types";
import { CreatePolicyComponentGeneralStepValidationErrors, CreatePolicyComponentTriggerConfigurationConditionValidationErrors, CreatePolicyComponentTriggerConfigurationValidationErrors, CreatePolicyComponentValidationErrors } from "@idw-types/common/validation/create-policy-component/create-policy-component-validation-error";
import { FormattedPolicyComponent, FormattedPolicyComponentCondition } from "@idw-types/data/reference-data/generic/policy-component";

export const getCreatePolicyComponentValidationErrors = (component: FormattedPolicyComponent, isComponentNameAlreadyUsed: boolean): CreatePolicyComponentValidationErrors => {
    const generalStepErrors = getCreatePolicyComponentGeneralStepValidationErrors(component, isComponentNameAlreadyUsed);
    const triggerConfigurationErrors = getCreationPolicyComponentTriggerConfigurationValidationErrors(component.conditions);

    const count = generalStepErrors.count + triggerConfigurationErrors.count;
    return {
        count,
        generalStep: generalStepErrors,
        triggerConfiguration: triggerConfigurationErrors
    }
}

const getCreatePolicyComponentGeneralStepValidationErrors = (component: FormattedPolicyComponent, isComponentNameAlreadyUsed: boolean): CreatePolicyComponentGeneralStepValidationErrors => {
    let count = 0;
    let nameError = null;
    let descriptionError = null;
    let categoryError = null;

    const { name, description, category } = component;

    if (name === "") {
        count++;
        nameError = getErrorAlert(resources.common.requiredField);
    } else {
        if (isComponentNameAlreadyUsed) {
            count++;
            nameError = getErrorAlert("This component name already exists");
        }
    }

    if (description === "") {
        count++;
        descriptionError = getErrorAlert(resources.common.requiredField);
    }

    if (category === null) {
        count++;
        categoryError = getErrorAlert(resources.common.requiredField);
    }

    return {
        count,
        nameError,
        descriptionError,
        categoryError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.policyComponentDetails.tabs.generalInformation.title} errorCount={count} />
    }
}

const getCreationPolicyComponentTriggerConfigurationValidationErrors = (conditions: Array<FormattedPolicyComponentCondition>): CreatePolicyComponentTriggerConfigurationValidationErrors => {
    let count = 0;
    let atLeastOneConditionAddedError = null;
    let conditionErrors = {};

    if (conditions.length === 0) {
        count++;
        atLeastOneConditionAddedError = getErrorAlert("One or more conditions must be added for this component");
    } else {
        conditionErrors = conditions.reduce((validationErrors, condition) => {
            const { uuid, triggerType } = condition;
            let conditionErrorsCount = 0;
            let assertionError = null;
            let assertionStatusError = null;
            let attributeFieldError = null;
            let policyComponentError = null;
            let policyComponentStatusError = null;
            let comparisonError = null;
            let policyComponentValueError = null;
            let serviceOfferingError = null;


            if (triggerType === TriggerTypes.ASSERTION) {
                if ((!policyConfigDecisionOperatorsWithNoValue.includes(condition.policyConditionOperator))&& (condition.values === null || condition.values === "")) {
                    conditionErrorsCount++;
                    assertionStatusError = getErrorAlert(resources.common.requiredField);
                }

                if (condition.serviceOfferingID === null) {
                    conditionErrorsCount++;
                    serviceOfferingError = getErrorAlert(resources.common.requiredField);
                }

                if (condition.assertionID === null) {
                    conditionErrorsCount++;
                    assertionError = getErrorAlert(resources.common.requiredField);
                }
            }

            if (triggerType === TriggerTypes.USER_INPUT || triggerType === TriggerTypes.ACQUIRED_DATA) {
                if (condition.attributeFieldID === null) {
                    conditionErrorsCount++;
                    attributeFieldError = getErrorAlert(resources.common.requiredField);
                }

                if (condition.policyConditionOperator === null) {
                    conditionErrorsCount++;
                    comparisonError = getErrorAlert(resources.common.requiredField);
                }

                if ((!policyConfigDecisionOperatorsWithNoValue.includes(condition.policyConditionOperator))&& (condition.values === null || condition.values === "")) {
                    conditionErrorsCount++;
                    policyComponentStatusError = getErrorAlert(resources.common.requiredField);
                    policyComponentValueError = getErrorAlert(resources.common.requiredField);
                }
            }

            if (triggerType === TriggerTypes.ACQUIRED_DATA) {
                if (condition.serviceOfferingID === null) {
                    conditionErrorsCount++;
                    serviceOfferingError = getErrorAlert(resources.common.requiredField);
                }
            }

            if (triggerType === TriggerTypes.COMPONENT) {
                if (condition.componentID === null) {
                    conditionErrorsCount++;
                    policyComponentError = getErrorAlert(resources.common.requiredField);
                }

                if ((!policyConfigDecisionOperatorsWithNoValue.includes(condition.policyConditionOperator))&& (condition.values === null || condition.values === "")) {
                    policyComponentValueError = getErrorAlert(resources.common.requiredField);
                }
            }

            validationErrors[uuid] = {
                count: conditionErrorsCount,
                serviceOfferingError,
                assertionError,
                assertionStatusError,
                attributeFieldError,
                policyComponentError,
                policyComponentStatusError,
                comparisonError,
                policyComponentValueError
            };

            count += conditionErrorsCount;

            return validationErrors;
        }, {} as { [conditionUuid: string]: CreatePolicyComponentTriggerConfigurationConditionValidationErrors });
    }

    return {
        count,
        atLeastOneConditionAddedError,
        conditionErrors,
        tabTitle: <TabTitleWithErrorCount tabName={resources.modals.createPolicyComponent.steps.triggerConfiguration.title} errorCount={count} />
    }
}

const getErrorAlert = (message: string): React.ReactNode => {
    return <ErrorAlert><span>{message}</span></ErrorAlert>
}